import * as Sentry from '@sentry/react';
import type { ReactElement, ReactNode } from 'react';

interface ErrorBoundaryProps {
    children: ReactNode;
    /** Optional custom fallback UI — must be a ReactElement */
    fallback?: ReactElement;
}

/**
 * SentryErrorBoundary — wraps a subtree.
 * Automatically reports uncaught errors to Sentry with component stack.
 *
 * Usage:
 *   <SentryErrorBoundary>
 *     <MyPage />
 *   </SentryErrorBoundary>
 *
 *   // With custom fallback:
 *   <SentryErrorBoundary fallback={<p>Something went wrong.</p>}>
 *     <MyPage />
 *   </SentryErrorBoundary>
 */
function SentryErrorBoundary({ children, fallback }: ErrorBoundaryProps) {
    return (
        <Sentry.ErrorBoundary
            fallback={fallback ?? <DefaultFallback />}
            showDialog
        >
            {children}
        </Sentry.ErrorBoundary>
    );
}

function DefaultFallback() {
    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6 text-center">
            <div className="max-w-md">
                <h1 className="text-xl font-semibold text-gray-800 mb-2">Something went wrong</h1>
                <p className="text-sm text-gray-500 mb-6">
                    An unexpected error occurred. Our team has been notified automatically.
                </p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-5 py-2 rounded-md bg-[#2563EB] text-white text-sm font-medium hover:bg-[#1d4ed8] transition"
                >
                    Reload page
                </button>
            </div>
        </div>
    );
}

export default SentryErrorBoundary;
